<template>
  <div @mouseleave="onMouseLeave">
    <nav>
      <ul
        class="flex items-center gap-x-6 overflow-auto snap-x snap-mandatory styled-scrollbars max-w-[calc(44vw-155px)]"
      >
        <li v-for="link in navigation" :key="link.id" class="flex-shrink-0">
          <component
            :is="getComponentType(link)"
            :id="
              link.url.includes('btn-id-') ? extractButtonId(link.url) : null
            "
            :to="
              !link.is_title && !link.url.includes('btn-id-')
                ? localePath(link.url)
                : null
            "
            class="inline-flex ui-text-m text-white"
            :class="[
              !link.is_title && 'cursor-pointer hover:underline',
              menuOpened && activeMenu?.id === link.id && '',
              Number(activeLink?.id) === link.id && '',
            ]"
            @mouseenter="setActiveMenu(link)"
            @mouseleave="onMouseLeave"
          >
            {{ link?.currentTranslation?.title }}
          </component>
        </li>
      </ul>
    </nav>
    <teleport to="body">
      <transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-100 ease-out"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="menuOpened"
          class="border-t border-white fixed left-0 right-0 top-header-top-with-discount z-[99999] max-h-[calc(100dvh-(var(--header-height)+var(--discount-compaign-visible-height)))] overflow-auto hide-mobile"
          @mouseenter="setActiveMenu(activeMenu)"
          @mouseleave="onMouseLeave"
        >
          <div class="flex w-full bg-primary">
            <div class="container px-0 flex">
              <div class="pt-16 pb-12 px-4 lg:px-8 grow">
                <div class="flex flex-wrap gap-y-10 gap-x-10 2xl:gap-x-20">
                  <div v-for="link in filteredChildren" :key="link.id">
                    <component
                      :is="getComponentType(link)"
                      :id="
                        link.url.includes('btn-id-')
                          ? extractButtonId(link.url)
                          : null
                      "
                      :to="
                        !link.is_title && !link.url.includes('btn-id-')
                          ? localePath(link.url)
                          : null
                      "
                      class="mb-8 inline-flex ui-text-xs font-medium text-white max-w-70"
                      :class="[
                        !link.is_title &&
                          'cursor-pointer underline-offset-4 hover:underline',
                      ]"
                    >
                      <span v-html="link?.currentTranslation?.title" />
                    </component>
                    <div class="flex gap-x-6 2xl:gap-x-8">
                      <ul
                        v-for="(
                          linkChunk, linkChunkIndex
                        ) in splitArrayToChunks(link.children as NavMenuItem[])"
                        :key="`menu-column-${link.id}-${linkChunkIndex}`"
                        class="space-y-2 max-h-42"
                      >
                        <li
                          v-for="link2 in linkChunk"
                          :key="link2.id"
                          class="ui-text-m text-white max-w-50"
                        >
                          <component
                            :is="getComponentType(link2)"
                            :id="
                              link2.url.includes('btn-id-')
                                ? extractButtonId(link2.url)
                                : null
                            "
                            :to="
                              !link2.is_title && !link2.url.includes('btn-id-')
                                ? localePath(link2.url)
                                : null
                            "
                            class="inline-flex items-center gap-2"
                            :class="[
                              !link2.is_title &&
                                'cursor-pointer underline-offset-1 hover:underline',
                              Number(activeLink?.id) === link2.id
                                ? 'text-white'
                                : '',
                            ]"
                          >
                            <img
                              v-if="link2.icon"
                              class="h-4 w-4 object-contain"
                              :src="link2.icon"
                              :alt="link2?.currentTranslation?.title"
                            />
                            <span v-html="link2?.currentTranslation?.title" />
                          </component>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="w-full flex-[0_0_400px] max-w-[400px] xl:flex-[0_0_625px] xl:max-w-[625px] min-h-[360px] max-xl:pt-16"
              >
                <CommonAppHeaderMenuSlider
                  v-if="sliderElement?.children?.length"
                  :slides="sliderElement.children"
                />
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
    <CommonAppHeaderMenuOverlay :show="!!menuOpened" />
  </div>
</template>

<script setup lang="ts">
import { NavMenuItem } from "ecom-types";
import { NuxtLink } from "#components";

const emit = defineEmits(["update:activeMenu"]);

const { headerMenu } = useGeneralStore();

const localePath = useLocalePathPolyfill();

const activeMenu = ref<TreeItem<NavMenuItem> | null>(null);
let timeout: ReturnType<typeof setTimeout>;

const navigation = computed(() => {
  return toTree(headerMenu);
});

const { findBreadcrumb } = useBreadcrumbs();

const route = useRoute();

watch(
  () => route.fullPath,
  () => {
    setActiveMenu(null);
  },
  {
    immediate: true,
  },
);

const menuOpened = computed(() => {
  return activeMenu.value && activeMenu.value.children?.length;
});

const activeLink = computed(() => {
  const breadcrumbs = findBreadcrumb(navigation.value, route);
  return breadcrumbs[breadcrumbs.length - 1];
});

const filteredChildren = computed(
  () =>
    activeMenu?.value?.children?.filter(
      (item) =>
        item.currentTranslation?.title.toLowerCase() !== "slider-elements",
    ) || [],
);

const sliderElement = computed(() =>
  activeMenu?.value?.children?.find(
    (item) =>
      item.currentTranslation?.title.toLowerCase() === "slider-elements",
  ),
);

function onMouseLeave() {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    setActiveMenu(null);
  }, 350);
}

function setActiveMenu(item: TreeItem<NavMenuItem> | null) {
  clearTimeout(timeout);
  activeMenu.value = item;
  emit("update:activeMenu", activeMenu.value);
}

function getComponentType(link: NavMenuItem) {
  if (link.is_title) {
    return "div";
  } else if (link.url.includes("btn-id-")) {
    return "button";
  } else {
    return NuxtLink;
  }
}

function splitArrayToChunks(array: NavMenuItem[]): NavMenuItem[][] {
  const sourceArray = array?.length ? JSON.parse(JSON.stringify(array)) : [];
  const chunkArray: NavMenuItem[][] = [];
  const chunkSize = 5;
  for (let i = 0; i < sourceArray.length; i += chunkSize) {
    chunkArray.push(sourceArray.slice(i, i + chunkSize));
  }
  return chunkArray;
}
</script>
